import React, { useState, useEffect, useRef } from "react";
import { TextField, List, ListItem, CircularProgress, Box, Button, Modal, Typography, Divider } from "@mui/material";
import GpsFixedIcon from "@mui/icons-material/GpsFixed";
import CheckCircleIcon from "@mui/icons-material/CheckCircle";
import { getHeaders, GoogleMapsAPIKey, dataConnect } from "../firebaseConfig";
import { tourLocationSearch } from "@firebasegen/default-connector";
import { resolvePlace } from "../services/placeService";
import { GoogleMap } from '@react-google-maps/api';
import { Marker } from "@react-google-maps/api";
import { add } from "date-fns";
import { GoogleMapsContext } from "../constants/GoogleMapsProvider";
import { useContext } from "react";


// Extract address component for both Google Maps v1/v3 APIs
const extractAddressComponent = (components, type) => {
    const comp = Array.isArray(components) ? components : components?.addressComponents;
    const match = comp?.find((c) => c.types?.includes(type));
    return match ? (match.longText || match.long_name || match.shortText || "") : "";
};

// Reverse Geocode function: fetches address info from lat/lng using Google Maps Geocoding API
const reverseGeocode = async (lat, lng, setReverseOptions, setReverseDialogOpen) => {
    try {
        const response = await fetch(`https://maps.googleapis.com/maps/api/geocode/json?latlng=${lat},${lng}&key=${GoogleMapsAPIKey}`);
        const data = await response.json();
        console.log("Reverse geocode data:", data);
        if (data.status === "OK" && data.results.length > 0) {
            const options = data.results.map((result) => ({
                formattedAddress: result.formatted_address,
                address: extractAddressComponent(result.address_components, "route") + " " + extractAddressComponent(result.address_components, "street_number"),
                city: extractAddressComponent(result.address_components, "locality") || extractAddressComponent(result.address_components, "postal_town"),
                zipCode: extractAddressComponent(result.address_components, "postal_code"),
                country: extractAddressComponent(result.address_components, "country"),
            }));
            setReverseOptions(options);
            setReverseDialogOpen(true);
        }
    } catch (error) {
        console.error("Error during reverse geocoding:", error);
    }
};

const LocationSearchField = ({
    location,
    onChange,
    artistId,
    fieldKey,
    suggestedLocations = [],
}) => {
    const { isLoaded } = useContext(GoogleMapsContext);
    const [locationName, setLocationName] = useState(location?.name || "");
    const [debouncedSearch, setDebouncedSearch] = useState();
    const [locationSelected, setLocationSelected] = useState(false);
    const [locationCoords, setLocationCoords] = useState(null);
    const [locationResults, setLocationResults] = useState([]);
    const [showLocationList, setShowLocationList] = useState(false);
    const [locationLoading, setLocationLoading] = useState(false);
    const [locationId, setLocationId] = useState("");
    const [hasTyped, setHasTyped] = useState(false);
    const [pendingLocation, setPendingLocation] = useState(null);
    const [editableLocation, setEditableLocation] = useState(null);
    const [reverseOptions, setReverseOptions] = useState([]);
    const [reverseDialogOpen, setReverseDialogOpen] = useState(false);

    const mapRef = useRef(null);

    useEffect(() => {
        if (!hasTyped) return;

        const handler = setTimeout(() => {
            if (!locationSelected) {
                setDebouncedSearch(locationName);
            }
        }, 500);

        return () => clearTimeout(handler);
    }, [locationName]);

    useEffect(() => {
        if (location && location.name && !locationSelected) {
            setLocationName(location.name);
        }
    }, [location?.id]);

    useEffect(() => {
        if (debouncedSearch) {
            fetchLocations(debouncedSearch);
        }
    }, [debouncedSearch]);



    const handleLocationSearch = (searchTerm) => {
        setHasTyped(true);
        setLocationSelected(false);
        setLocationName(searchTerm);
        setLocationId("");
        setLocationCoords(null);
        setPendingLocation(null);
        setEditableLocation(null);
    };

    const fetchLocations = async (searchTerm) => {
        if (searchTerm.length < 3) {
            setLocationResults([]);
            setShowLocationList(false);
            return;
        }

        setLocationLoading(true);
        try {
            const { data } = await tourLocationSearch(dataConnect, { s: searchTerm });
            if (data?.venues?.length > 0) {
                setLocationResults(data.venues);
                setShowLocationList(true);
            } else {
                searchGooglePlaces(searchTerm);
            }
        } catch (error) {
            console.error("Error searching location:", error);
        }
        setLocationLoading(false);
    };

    const handleSelectLocation = async (locationData) => {
        setLocationSelected(true);
        setShowLocationList(false);
        setLocationResults([]);
        setLocationName(locationData.name);

        if (locationData.id !== undefined) {
            setLocationId(locationData.id);
            setLocationCoords(locationData.location.latitude && locationData.location.longitude ? { lat: locationData.location.latitude, lng: locationData.location.longitude } : null);
            onChange({
                id: locationData.id,
                name: editableLocation?.name || "",
                location: {
                    latitude: locationData.location.latitude,
                    longitude: locationData.location.longitude,
                    address: editableLocation?.address || "",
                }
            });
            //onChange(locationData.id);
            setPendingLocation(null);
            setEditableLocation(null);
            return;
        }

        // For locations without id, set pendingLocation to show map and save button
        setPendingLocation(locationData);
        setEditableLocation({
            name: locationData.name || "",
            address: locationData.address || "",
            city: locationData.city || "",
            zipCode: locationData.zipCode || "",
            country: locationData.country || "",
            formattedAddress: locationData.formatted_address || "",
            website: locationData.website || "",
            phoneNumber: locationData.phoneNumber || "",
            photoUrl: locationData.photoUrl || "",
            capacity: "" // Manual entry usually
        });
        // Updated: Support both direct values and functions for lat/lng (Google Maps API)
        const lat = typeof locationData.geometry?.location?.lat === "function"
            ? locationData.geometry.location.lat()
            : locationData.geometry?.location?.lat;

        const lng = typeof locationData.geometry?.location?.lng === "function"
            ? locationData.geometry.location.lng()
            : locationData.geometry?.location?.lng;

        if (lat && lng) {
            setLocationCoords({ lat, lng });
        }
        setLocationId("");
    };

    const searchGooglePlaces = async (searchTerm) => {
        try {
            if (!GoogleMapsAPIKey) {
                console.error("Google Maps API Key is missing! Check your .env file.");
                return;
            }

            const response = await fetch(`https://places.googleapis.com/v1/places:searchText`, {
                method: "POST",
                headers: {
                    "Content-Type": "application/json",
                    "X-Goog-Api-Key": GoogleMapsAPIKey,
                    "X-Goog-FieldMask": "places.id,places.displayName,places.formattedAddress,places.location,places.addressComponents,places.websiteUri,places.internationalPhoneNumber,places.photos"
                },
                body: JSON.stringify({
                    textQuery: searchTerm
                })
            });

            const data = await response.json();
            if (data.places && data.places.length > 0) {
                const results = data.places.map((place) => {

                    let photoUrl = null;
                    if (place.photos && place.photos.length > 0) {
                        const photoReference = place.photos[0].name; // Format: "places/{placeId}/photos/{photoId}"
                        // Google Places API (New) returns media resource name.
                        // Use: https://places.googleapis.com/v1/{photoReference}/media?key=API_KEY&maxHeightPx=400&maxWidthPx=400
                        photoUrl = `https://places.googleapis.com/v1/${photoReference}/media?key=${GoogleMapsAPIKey}&maxHeightPx=400&maxWidthPx=400`;
                    }

                    return {
                        google: true,
                        name: place.displayName.text,
                        formatted_address: place.formattedAddress,
                        city: extractAddressComponent(place.addressComponents, "locality") || extractAddressComponent(place.addressComponents, "postal_town") || extractAddressComponent(place.addressComponents, "administrative_area_level_3") || "",
                        zipCode: extractAddressComponent(place.addressComponents, "postal_code"),
                        country: extractAddressComponent(place.addressComponents, "country"),
                        address: `${extractAddressComponent(place.addressComponents, "route") || ""} ${extractAddressComponent(place.addressComponents, "street_number") || ""}`.trim() || place.name,
                        geometry: { location: { lat: place.location.latitude, lng: place.location.longitude } },
                        place_id: place.id,
                        website: place.websiteUri,
                        phoneNumber: place.internationalPhoneNumber,
                        photoUrl: photoUrl
                    };
                });
                setLocationResults(results);
                setShowLocationList(true);
            } else {
                setLocationResults([]);
            }
        } catch (error) {
            console.error("Error fetching Google Places:", error);
        }
    };



    const handleSaveLocation = async () => {
        if (!pendingLocation || !locationCoords || !editableLocation) return;
        try {
            // Reuses the location if this place is already in Tourminator
            const { locationId: newLocationId } = await resolvePlace({
                name: editableLocation.name,
                formattedAddress: editableLocation.formattedAddress || null,
                address: editableLocation.address || null,
                city: editableLocation.city || null,
                zipCode: editableLocation.zipCode || null,
                country: editableLocation.country || null,
                latitude: locationCoords.lat,
                longitude: locationCoords.lng,
                googleId: pendingLocation.place_id || null,
            }, { artistId });
            setLocationId(newLocationId);
            setLocationSelected(true);

            // Pass extended venue details back to parent (VenueSearchField)
            onChange({
                id: newLocationId,
                name: editableLocation?.name || "",
                location: {
                    latitude: locationCoords.lat,
                    longitude: locationCoords.lng,
                    address: editableLocation?.address || "",
                },
                website: editableLocation?.website || "",
                phoneNumber: editableLocation?.phoneNumber || "",
                capacity: editableLocation?.capacity || "",
                photoUrl: editableLocation?.photoUrl || ""
            });

            setPendingLocation(null);
            setEditableLocation(null);

        } catch (error) {
            console.error("Error creating location:", error);
        }
    };

    return (
        <Box>
            {suggestedLocations?.length > 0 && !locationSelected && !hasTyped && (
                <List sx={{ maxHeight: 200, overflow: 'auto', bgcolor: 'background.paper', mt: 1, border: '1px solid #ccc', borderRadius: 1 }}>
                    {suggestedLocations.map((loc, index) => (
                        <ListItem button key={index} onClick={() => handleSelectLocation(loc)} sx={{ cursor: 'pointer' }}>
                            {loc.name}
                        </ListItem>
                    ))}
                    <ListItem button sx={{ cursor: 'pointer', fontStyle: 'italic' }} onClick={() => setHasTyped(true)}>
                        Other...
                    </ListItem>
                </List>
            )}
            {
                ((suggestedLocations?.length === 0) || locationSelected || hasTyped) && (
                    <>
                        <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                            <TextField
                                type="text"
                                value={locationName}
                                key={fieldKey}
                                onChange={(e) => handleLocationSearch(e.target.value)}
                                placeholder="Search or enter a location"
                                fullWidth
                                variant="outlined"
                                size="small"
                                InputProps={{
                                    endAdornment: (
                                        <>
                                            {locationLoading && <CircularProgress size={20} />}
                                            {locationSelected && <CheckCircleIcon sx={{ color: '#238636', ml: 1 }} />}
                                        </>
                                    )
                                }}
                                sx={{
                                    '& .MuiOutlinedInput-root': {
                                        '& fieldset': {
                                            borderColor: locationSelected ? '#238636' : 'rgba(0, 0, 0, 0.23)',
                                            borderWidth: locationSelected ? 2 : 1,
                                        },
                                        '&:hover fieldset': {
                                            borderColor: locationSelected ? '#2ea043' : 'rgba(0, 0, 0, 0.87)',
                                        },
                                        '&.Mui-focused fieldset': {
                                            borderColor: locationSelected ? '#238636' : '#1976d2',
                                        },
                                    },
                                }}
                            />
                            <Button variant="outlined" onClick={() => {
                                navigator.geolocation.getCurrentPosition(
                                    (position) => {
                                        const { latitude, longitude } = position.coords;
                                        setLocationCoords({ lat: latitude, lng: longitude });
                                        reverseGeocode(latitude, longitude, setReverseOptions, setReverseDialogOpen);
                                    },
                                    (error) => {
                                        console.error("Geolocation error:", error);
                                        alert("Could not get current location.");
                                    }
                                );
                            }}>
                                <GpsFixedIcon />
                            </Button>
                        </Box>
                        {locationLoading && <CircularProgress size={20} sx={{ ml: 1, verticalAlign: 'middle' }} />}
                        {/* Reverse Geocode Options Dropdown */}
                        {reverseDialogOpen && reverseOptions.length > 0 && (
                            <List sx={{ maxHeight: 300, overflow: 'auto', border: '1px solid #ccc', borderRadius: 1, mt: 1 }}>
                                {reverseOptions.map((option, index) => (
                                    <ListItem
                                        key={index}
                                        button
                                        onClick={async () => {
                                            try {
                                                const { locationId: newLocationId } = await resolvePlace({
                                                    name: option.name || option.formattedAddress,
                                                    formattedAddress: option.formattedAddress,
                                                    address: option.address,
                                                    city: option.city,
                                                    zipCode: option.zipCode,
                                                    country: option.country,
                                                    latitude: locationCoords.lat,
                                                    longitude: locationCoords.lng,
                                                }, { artistId });
                                                setLocationId(newLocationId);
                                                setLocationSelected(true);
                                                setPendingLocation(null);
                                                setEditableLocation(null);
                                                setLocationName(option.formattedAddress);
                                                setReverseDialogOpen(false);
                                                onChange({
                                                    id: newLocationId,
                                                    name: editableLocation?.name || "",
                                                    location: {
                                                        latitude: locationCoords.lat,
                                                        longitude: locationCoords.lng,
                                                        address: editableLocation?.address || "",
                                                    }
                                                });
                                                //onChange(newLocationId);
                                            } catch (error) {
                                                console.error("Error creating location from reverse option:", error);
                                            }
                                        }}
                                    >
                                        {option.formattedAddress}
                                    </ListItem>
                                ))}
                            </List>
                        )}
                    </>
                )
            }
            {
                showLocationList && locationResults.length > 0 && (
                    <List sx={{ maxHeight: 200, overflow: 'auto', bgcolor: 'background.paper', mt: 1, border: '1px solid #ccc', borderRadius: 1 }}>
                        {
                            locationResults.map((result, index) => (
                                <ListItem button key={index} onClick={() => handleSelectLocation(result)} sx={{ cursor: 'pointer' }}>
                                    {result.name} {result.formatted_address ? `(${result.formatted_address})` : ""}
                                </ListItem>
                            ))
                        }
                    </List>
                )
            }
            {/* Pending Location Modal */}
            <Modal
                open={!!pendingLocation}
                onClose={() => {
                    setPendingLocation(null);
                    setEditableLocation(null);
                }}
            >
                <Box sx={{
                    position: 'absolute',
                    top: '50%',
                    left: '50%',
                    transform: 'translate(-50%, -50%)',
                    width: '90%',
                    maxWidth: 600,
                    bgcolor: 'background.paper',
                    color: 'text.primary',
                    border: '1px solid',
                    borderColor: 'divider',
                    boxShadow: 24,
                    p: 4,
                    borderRadius: 2,
                    maxHeight: '90vh',
                    overflowY: 'auto'
                }}>
                    <Typography variant="h6" sx={{ mb: 2 }}>
                        Confirm Location Details
                    </Typography>
                    <Divider sx={{ mb: 3 }} />

                    {locationCoords && isLoaded && (
                        <Box sx={{ mb: 3 }}>
                            <GoogleMap
                                mapContainerStyle={{ height: "300px", width: "100%", borderRadius: '8px', border: '1px solid', borderColor: 'divider' }}
                                center={locationCoords}
                                zoom={15}
                                onLoad={map => { mapRef.current = map; }}
                                onClick={(e) => {
                                    const newLat = e.latLng.lat();
                                    const newLng = e.latLng.lng();
                                    setLocationCoords({ lat: newLat, lng: newLng });
                                    reverseGeocode(newLat, newLng, setReverseOptions, setReverseDialogOpen);
                                }}
                            >
                                {typeof locationCoords.lat === "number" && typeof locationCoords.lng === "number" && (
                                    <Marker
                                        key={`${locationCoords.lat}-${locationCoords.lng}`}
                                        position={locationCoords}
                                        draggable={true}
                                        onDragEnd={(e) => {
                                            const newLat = e.latLng.lat();
                                            const newLng = e.latLng.lng();
                                            setLocationCoords({ lat: newLat, lng: newLng });
                                            reverseGeocode(newLat, newLng, setReverseOptions, setReverseDialogOpen);
                                        }}
                                    />
                                )}
                            </GoogleMap>
                        </Box>
                    )}

                    <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
                        <TextField
                            label="Name"
                            value={editableLocation?.name || ""}
                            onChange={(e) => setEditableLocation(prev => ({ ...prev, name: e.target.value }))}
                            fullWidth
                            size="small"
                        />
                        <TextField
                            label="Address"
                            value={editableLocation?.address || ""}
                            onChange={(e) => setEditableLocation(prev => ({ ...prev, address: e.target.value }))}
                            fullWidth
                            size="small"
                        />
                        <Box sx={{ display: 'flex', gap: 2 }}>
                            <TextField
                                label="City"
                                value={editableLocation?.city || ""}
                                onChange={(e) => setEditableLocation(prev => ({ ...prev, city: e.target.value }))}
                                fullWidth
                                size="small"
                            />
                            <TextField
                                label="Zip Code"
                                value={editableLocation?.zipCode || ""}
                                onChange={(e) => setEditableLocation(prev => ({ ...prev, zipCode: e.target.value }))}
                                fullWidth
                                size="small"
                            />
                        </Box>
                        <TextField
                            label="Country"
                            value={editableLocation?.country || ""}
                            onChange={(e) => setEditableLocation(prev => ({ ...prev, country: e.target.value }))}
                            fullWidth
                            size="small"
                        />
                        <TextField
                            label="Formatted Address"
                            value={editableLocation?.formattedAddress || ""}
                            onChange={(e) => setEditableLocation(prev => ({ ...prev, formattedAddress: e.target.value }))}
                            fullWidth
                            size="small"
                        />
                        <Box sx={{ display: 'flex', gap: 2 }}>
                            <TextField
                                label="Latitude"
                                value={locationCoords?.lat || ''}
                                onChange={(e) => {
                                    const newLat = parseFloat(e.target.value);
                                    if (!isNaN(newLat)) setLocationCoords((prev) => ({ ...prev, lat: newLat }));
                                }}
                                fullWidth
                                size="small"
                                type="number"
                                inputProps={{ step: "any" }}
                            />
                            <TextField
                                label="Longitude"
                                value={locationCoords?.lng || ''}
                                onChange={(e) => {
                                    const newLng = parseFloat(e.target.value);
                                    if (!isNaN(newLng)) setLocationCoords((prev) => ({ ...prev, lng: newLng }));
                                }}
                                fullWidth
                                size="small"
                            />
                        </Box>
                        <TextField
                            label="Website"
                            value={editableLocation?.website || ""}
                            onChange={(e) => setEditableLocation(prev => ({ ...prev, website: e.target.value }))}
                            fullWidth
                            size="small"
                        />
                        <Box sx={{ display: 'flex', gap: 2 }}>
                            <TextField
                                label="Phone Number"
                                value={editableLocation?.phoneNumber || ""}
                                onChange={(e) => setEditableLocation(prev => ({ ...prev, phoneNumber: e.target.value }))}
                                fullWidth
                                size="small"
                            />
                            <TextField
                                label="Capacity"
                                value={editableLocation?.capacity || ""}
                                onChange={(e) => setEditableLocation(prev => ({ ...prev, capacity: e.target.value }))}
                                fullWidth
                                size="small"
                                type="number"
                            />
                        </Box>

                        {editableLocation?.photoUrl && (
                            <Box
                                component="img"
                                sx={{
                                    height: 200,
                                    width: '100%',
                                    objectFit: 'cover',
                                    borderRadius: 1,
                                    border: '1px solid',
                                    borderColor: 'divider',
                                    mt: 2
                                }}
                                alt="Venue Photo"
                                src={editableLocation.photoUrl}
                            />
                        )}
                    </Box>

                    <Box sx={{ mt: 3, display: 'flex', justifyContent: 'flex-end', gap: 2 }}>
                        <Button
                            variant="outlined"
                            onClick={() => {
                                setPendingLocation(null);
                                setEditableLocation(null);
                            }}
                        >
                            Cancel
                        </Button>
                        <Button
                            variant="contained"
                            onClick={handleSaveLocation}
                            sx={{ bgcolor: 'success.main', '&:hover': { bgcolor: 'success.dark' } }}
                        >
                            Set location
                        </Button>
                    </Box>
                </Box>
            </Modal>
        </Box >
    );
};

export default LocationSearchField;
